import { reactive } from 'vue'

export type NotificationVariant =
  | 'info'
  | 'success'
  | 'error'
  | 'warning'
  | 'unique'

export interface NotificationAction {
  label: string
  onClick: () => void
}

export interface NotificationProps {
  variant: NotificationVariant
  title?: string
  message?: string
  duration?: number
  actions?: NotificationAction[]
  /**
   * Determines if the notification can be dismissed by other notification.
   * Defaults to `true`
   */
  dismissible?: boolean
}

export interface Notification extends NotificationProps {
  /**
   * Unique identifier generated from the {@link NotificationProps.title}
   * and the {@link NotificationProps.message}.
   * This can be passed in to {@link AppNotifications.dismiss} method to
   * dismiss the notification.
   */
  id: number
}

/**
 * How long (in milliseconds) the notification will
 * show for before being auto dismissed
 */
const NOTIFICATION_DEFAULT_DURATION = 5000

export class AppNotifications {
  private _state = reactive<{ notifications: Notification[] }>({
    notifications: [],
  })

  public get notifications(): Notification[] {
    return this._state.notifications
  }

  /**
   * Generate a unique ID based on notification content.
   * Notifications with the same title and message will have the same ID,
   * which prevents duplicates.
   */
  private _generateId(title?: string, message?: string): number {
    const key = `${title || ''}-${message || ''}`
    // Simple hash function to convert string to number
    return Math.abs(
      key.split('').reduce((hash, char) => {
        return (hash << 5) - hash + char.charCodeAt(0)
      }, 0),
    )
  }

  /**
   * Dismiss all notifications that are currently in view (active)
   * except for notifications that explicitly stated they can't be dismissed
   * (`notification.dismissible = true`).
   */
  private _dismissActiveDismissibleNotification(): void {
    this._state.notifications = this._state.notifications.filter(
      (notification) => notification.dismissible === false,
    )
  }

  private _show(notification: NotificationProps): number {
    const id = this._generateId(notification.title, notification.message)

    const existingNotification = this._state.notifications.find(
      (n) => n.id === id,
    )

    if (existingNotification) {
      // Notification with the same id already exist.
      // Return existing ID instead of creating new one
      return existingNotification.id
    }

    const newNotification = {
      id,
      dismissible: true,
      duration: NOTIFICATION_DEFAULT_DURATION,
      ...notification,
    } as const

    this._dismissActiveDismissibleNotification()

    this._state.notifications.push(newNotification)

    // Auto dismiss after duration
    // - if duration greater than 0, or
    // - if the notification has not explicitly set
    //   they should not be dismissed (dismissible = false)
    if (newNotification.duration > 0 && newNotification.dismissible !== false) {
      setTimeout(() => {
        this.dismiss(id)
      }, newNotification.duration)
    }

    return id
  }

  dismiss(id: number): void {
    const index = this._state.notifications.findIndex((n) => n.id === id)
    if (index > -1) {
      this._state.notifications.splice(index, 1)
    }
  }

  success(options: Omit<NotificationProps, 'variant'>): number {
    return this._show({ variant: 'success', ...options })
  }

  error(options: Omit<NotificationProps, 'variant'>): number {
    return this._show({ variant: 'error', ...options })
  }

  warning(options: Omit<NotificationProps, 'variant'>): number {
    return this._show({ variant: 'warning', ...options })
  }

  info(options: Omit<NotificationProps, 'variant'>): number {
    return this._show({ variant: 'info', ...options })
  }

  unique(options: Omit<NotificationProps, 'variant'>): number {
    return this._show({ variant: 'unique', ...options })
  }

  clearAll(): void {
    this._state.notifications = []
  }
}

export const appNotifications = new AppNotifications()
